import { getSupabase } from '../../components/supabaseClient';
import { getTarget } from './targetTables';

// A column added via the add-columns endpoint can lag in PostgREST's schema
// cache, so an insert issued right after the DDL may transiently fail with a
// "schema cache" / "Could not find the ... column" error. Retry those batches.
const SCHEMA_CACHE_RE = /schema cache|Could not find the .* column/i;
// A partial/missing unique index on the conflict key makes ON CONFLICT unusable
// for the whole table, so every batch 400s identically. When the caller supplies
// a `repairOnConflict` hook (the wizard wires it to the "Build patient tables"
// job, which turns a partial ehr_id index into a full one), we rebuild the table
// once and retry — treating the error as transient while PostgREST reloads.
const ARBITER_MISSING_RE = /no unique or exclusion constraint matching the ON CONFLICT/i;
const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Commit rows into the chosen target table. Each row is mapped through the
// target's toRecord transform first; rows the transform rejects (returns null,
// e.g. a patients_with_ehrid row with no EHR ID / primary key) are counted in
// `skipped` and never sent. Default target is notes-c for backward compatibility;
// the wizard passes 'patients_with_ehrid' explicitly.
export async function commitImport(rows, { target = 'notes-c', extraColumns = [], batchSize = 500, onProgress, getClient = getSupabase, schemaRetries = 3, sleep = defaultSleep, importDuplicates = false, repairOnConflict, mergedRecords = [], returnRows = false } = {}) {
  const t = typeof target === 'string' ? getTarget(target) : target;
  const client = await getClient();
  const extraNames = extraColumns.map((c) => c.name);

  let records = [];
  let skipped = 0;
  rows.forEach((row) => {
    const rec = t.toRecord(row, extraNames);
    if (rec == null) { skipped += 1; return; }
    records.push(rec);
  });

  // Pre-built merged records (duplicate resolution) are already DB-shaped — append
  // them as-is so they upsert with the same ON CONFLICT handling as the mapped rows.
  mergedRecords.forEach((rec) => { if (rec != null) records.push(rec); });

  // Importing duplicates into a conflict-keyed table (e.g. patients_with_ehrid,
  // keyed on ehr_id) means UPDATING the existing row via upsert — you can't insert
  // a second row with the same primary key. Postgres' ON CONFLICT DO UPDATE also
  // can't touch the same key twice in one statement, so collapse same-key records
  // first (last occurrence wins). Without this the whole batch would 400.
  if (importDuplicates && t.onConflict) {
    const byKey = new Map();
    records.forEach((rec) => byKey.set(rec[t.onConflict], rec));
    records = [...byKey.values()];
  }

  const total = records.length;
  let inserted = 0;
  // Opt-in: the DB-assigned rows (ids included). The schedule importer needs the
  // new notes-c id to send a patient their forms; every other caller ignores
  // this, and without the flag the request shape is exactly as before.
  const insertedRows = [];
  const errors = [];
  let repaired = false;   // the one-time table rebuild is shared across all batches

  for (let i = 0; i < total; i += batchSize) {
    const batchIndex = Math.floor(i / batchSize);
    const batch = records.slice(i, i + batchSize);
    try {
      let error;
      for (let attempt = 0; ; attempt += 1) {
        const q = client.from(t.table);
        // ignoreDuplicates:true -> ON CONFLICT DO NOTHING (skip existing rows);
        // false -> DO UPDATE (overwrite them). Driven by the wizard's "Don't
        // import duplicates" toggle. Tables with no conflict key just insert.
        const pending = t.onConflict
          ? q.upsert(batch, { onConflict: t.onConflict, ignoreDuplicates: !importDuplicates })
          : q.insert(batch);
        const res = await (returnRows ? pending.select() : pending);
        error = res.error;
        if (!error) {
          if (returnRows && Array.isArray(res.data)) insertedRows.push(...res.data);
          break;
        }
        const msg = error.message || '';
        const arbiterMissing = !!t.onConflict && ARBITER_MISSING_RE.test(msg);
        // First sighting of the arbiter error: rebuild the table once (the hook
        // runs the same code as the "Build patient tables" button, which turns a
        // partial ehr_id index into a full one so ON CONFLICT can use it).
        if (arbiterMissing && repairOnConflict && !repaired) {
          repaired = true;
          await repairOnConflict();
        }
        // Retry on schema-cache lag, or on the arbiter error right after a repair
        // (PostgREST needs a beat to pick up the rebuilt index).
        const retryable = SCHEMA_CACHE_RE.test(msg) || (arbiterMissing && repaired);
        if (attempt >= schemaRetries || !retryable) break;
        await sleep(500 * (attempt + 1));
      }
      if (error) errors.push({ batchIndex, message: error.message });
      else inserted += batch.length;
    } catch (e) {
      errors.push({ batchIndex, message: e.message || String(e) });
    }
    if (onProgress) onProgress(Math.min(i + batchSize, total), total);
  }

  return { inserted, errors, skipped, insertedRows };
}
